<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { getLabel } from '@/utils/data-display-utils';

import BaseSubscribe from '../base/BaseSubscribe.vue';

import { Module, ProjectModuleSubscriptionConfigDto } from '@/store/module.interface';
import { mailingListService } from '@/services/mailingList.service';
import { getTranslation } from '@/utils/data-display-utils';
import { constant } from '@/constant';
import { showToaster } from '@/utils/toaster-util';
import { Locales } from '@/store/language.interface';

// LIFECYCLE
onMounted(async () => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const code = urlSearchParams.get('code');
  if (code) {
    const successful = await mailingListService.verifyEmail(code);
    const translationKey = 'project-module.subscription.email-verification';
    showToaster({
      message: getLabel(`${translationKey}.${successful ? 'successful' : 'unsuccessful'}`, props.languageEnabled),
      type: successful ? 'positive' : 'negative',
    });
    if (successful) subscriberCount.value++;
  }
});

// CONST
const userSubscribed = ref<boolean>(false);
const emailAlreadyInList = ref<boolean>(false);
const emailNeedsVerification = ref<boolean>(false);
const subscriberCount = ref<number>(0);
const userSubscribeRef = ref<InstanceType<typeof BaseSubscribe>>();

// PROPS
const props = defineProps<{
  module: Module;
  languageEnabled?: Locales[];
}>();

const moduleConfig = props.module.config as ProjectModuleSubscriptionConfigDto;
userSubscribed.value = moduleConfig.isUserSubscribed;
subscriberCount.value = moduleConfig.subscriberCount;

// FUNCTIONS
async function subscribe(values) {
  const subscriptionStatus = await mailingListService.subscribe(
    constant.MailingList.Parent.project,
    props.module.project_id,
    values.email,
  );
  resetForm();
  switch (subscriptionStatus) {
    case constant.MailingList.SubscriptionStatus.AlreadySubscribed:
      emailAlreadyInList.value = true;
      break;
    case constant.MailingList.SubscriptionStatus.NeedEmailVerification:
      emailNeedsVerification.value = true;
      break;
    case constant.MailingList.SubscriptionStatus.NewlySubscribed:
      userSubscribed.value = true;
      subscriberCount.value++;
      break;
  }
}

async function unsubscribe() {
  const isUnsubscribed = await mailingListService.unsubscribe(
    constant.MailingList.Parent.project,
    props.module.project_id,
  );
  if (isUnsubscribed) {
    subscriberCount.value--;
    resetForm();
  }
}

function resetForm() {
  userSubscribed.value = false;
  emailNeedsVerification.value = false;
  emailAlreadyInList.value = false;
  userSubscribeRef.value?.resetForm();
}
</script>
<template>
  <div class="subscription-module">
    <div class="subscription-module__title">
      <q-icon name="o_email" />
      <h2>{{ getTranslation(props.module, 'title', languageEnabled) }}</h2>
    </div>
    <div class="subscription-module__content">
      <div class="subscription-module__content-text col">
        <span v-if="!userSubscribed">{{
          getLabel('project-module.subscription.status.not-subscribed', props.languageEnabled)
        }}</span>
        <span v-else>{{
          getLabel('project-module.subscription.status.already-subscribed', props.languageEnabled)
        }}</span>
        <span class="count">
          {{
            getLabel('project-module.subscription.subscriber-count', props.languageEnabled, {
              named: { subscriberCount },
              plural: subscriberCount,
            })
          }}
        </span>
      </div>
      <div class="subscription-module__content-subscribe">
        <base-subscribe
          ref="userSubscribeRef"
          :user-subscribed="userSubscribed"
          :email-already-in-list="emailAlreadyInList"
          :email-needs-verification="emailNeedsVerification"
          :input-filled="true"
          :language-enabled="props.languageEnabled"
          @unsubscribe="unsubscribe"
          @subscribe="subscribe"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.subscription-module {
  display: flex;
  flex-direction: column;
  background-color: $color-neutral-grey-blue-150;
  border-radius: 8px;
  gap: $space-sm;
  padding: $space-lg;

  &__title {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: $space-sm;

    .q-icon {
      font-size: $large-title;
    }
  }

  &__content {
    display: flex;
    flex-direction: row;
    gap: $space-sm;
    width: 100%;
    align-items: flex-start;

    &-text {
      display: flex;
      flex-direction: column;
      justify-content: center;
      width: 50%;

      .count {
        color: rgba(0, 0, 0, 0.5);
      }
    }

    &-subscribe {
      width: 50%;
      white-space: nowrap;

      .user-subscribe {
        display: flex;
        flex-direction: column;
        width: 100%;
        gap: $space-sm;

        .buttons {
          width: 100%;
          justify-content: end;
        }

        button,
        .base-inputs {
          width: 50%;
          height: 44px;
        }
      }
    }
  }

  .q-field {
    padding-bottom: 0;
  }
}

@media screen and (max-width: $breakpoint-md) {
  .subscription-module {
    &__content {
      flex-direction: column;

      &-actions {
        width: inherit;

        .buttons {
          flex-direction: column;

          .q-field,
          .q-btn {
            width: 100%;
          }
        }
      }

      &-text {
        width: inherit;
      }
    }
  }
}

@media screen and (max-width: $breakpoint-sm) {
  .subscription-module__content-subscribe {
    width: 100%;
  }
}
</style>
